import React, { useEffect, useRef } from "react";
import Select, {
  components as selectComponents,
  GroupBase,
  Props as ReactSelectProps,
  StylesConfig,
  components,
} from "react-select";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";

export type SelectOption = {
  value: string | number;
  label: string;
  isDisabled?: boolean;
};

export type SelectFieldProps<
  Option = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<ReactSelectProps<Option, IsMulti, Group>, "styles"> & {
  label?: string;
  errorMessage?: string;
  stylesOverride?: StylesConfig<Option, IsMulti, Group>;
  dropdownIndicator?: React.ReactNode;
  mode?: "single" | "multiple";
  testId?: string;
};

const MenuPortal = (props) => {
  return <components.MenuPortal {...props} className="react-select-menu-portal" />;
};

const OptionScroll = (props) => {
  const ref = useRef(null);

  useEffect(() => {
    if (props.isSelected && ref.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [props.isSelected]);

  return (
    <div ref={ref}>
      <components.Option {...props} />
    </div>
  );
};

const baseStyles = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(): StylesConfig<Option, IsMulti, Group> => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: 38,
    borderRadius: 8,
    backgroundColor: state.isDisabled ? "#fbf9fa" : "white",
    borderColor: state.isFocused ? "#2563eb" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(37,99,235,0.1)" : "none",
    ":hover": {
      borderColor: state.isFocused ? "#2563eb" : "#d1d5db",
    },
    fontSize: 14,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 8px",
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6b7280",
    fontWeight: 500,
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? "#6b7280" : "#111827",
    fontWeight: 500,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#eff6ff",
    borderRadius: 6,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#1f2937",
    fontWeight: 500,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    ":hover": {
      backgroundColor: "transparent",
      color: "#ef4444",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: 14,
    fontWeight: 500,
    backgroundColor: state.isSelected ? "#dbeafe" : state.isFocused ? "#f3f4f6" : "white",
    color: state.isDisabled ? "#6b7280" : "#111827",
    ":active": {
      backgroundColor: "#e5e7eb",
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: 8,
    overflow: "hidden",
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
});

function SelectField<
  Option = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: SelectFieldProps<Option, IsMulti, Group>) {
  const {
    label,
    errorMessage,
    stylesOverride,
    dropdownIndicator,
    mode,
    testId,
    menuPortalTarget = typeof window !== "undefined" ? document.body : undefined,
    ...rest
  } = props;

  // Determine multi-select mode: explicit isMulti prop takes precedence, then mode
  const computedIsMulti = (rest as unknown as { isMulti?: boolean }).isMulti ?? mode === "multiple";

  return (
    <div className="w-full" data-testid={testId}>
      {label ? (
        <label className="block text-sm font-semibold text-gray-800 mb-2">{label}</label>
      ) : null}
      <Select<Option, IsMulti, Group>
        menuPortalTarget={menuPortalTarget}
        isMulti={computedIsMulti as unknown as IsMulti}
        styles={{
          ...baseStyles<Option, IsMulti, Group>(),
          ...(stylesOverride || {}),
        }}
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: (indicatorProps) => (
            <selectComponents.DropdownIndicator {...indicatorProps}>
              {dropdownIndicator || <ChevronUpDownIcon className="w-3.5 h-3.5 text-gray-500" />}
            </selectComponents.DropdownIndicator>
          ),
          Option: OptionScroll,
          MenuPortal,
        }}
        {...(rest as ReactSelectProps<Option, IsMulti, Group>)}
      />
      {errorMessage ? <p className="mt-1 text-xs text-red-600">{errorMessage}</p> : null}
    </div>
  );
}

export default SelectField;
